import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CommentsService,
  CommentDto,
  CreateCommentDto,
  UpdateCommentDto,
} from '@services/CommentsService'

export interface CommentState {
  comments: CommentDto[]
  isLoading: boolean
  error: string | null
}

const initialState: CommentState = {
  comments: [],
  isLoading: false,
  error: null,
}

/**
 * Create a new comment
 */
export const doCreateComment = createAsyncThunk<CommentDto, CreateCommentDto>(
  'comment/create',
  async (dto, { rejectWithValue }) => {
    try {
      return await CommentsService.create(dto)
    } catch (error) {
      return rejectWithValue('Failed to create comment')
    }
  },
)

/**
 * Update an existing comment
 */
export const doUpdateComment = createAsyncThunk<
  CommentDto,
  { id: number; dto: UpdateCommentDto }
>('comment/update', async ({ id, dto }, { rejectWithValue }) => {
  try {
    return await CommentsService.update(id, dto)
  } catch (error) {
    return rejectWithValue('Failed to update comment')
  }
})

/**
 * Delete a comment
 */
export const doDeleteComment = createAsyncThunk<
  { commentId: number; orderId: number },
  { commentId: number; orderId: number }
>('comment/delete', async ({ commentId, orderId }, { rejectWithValue }) => {
  try {
    await CommentsService.delete(commentId)
    return { commentId, orderId }
  } catch (error) {
    return rejectWithValue('Failed to delete comment')
  }
})

/**
 * Fetch comments for an order
 */
export const doFetchCommentsByOrderId = createAsyncThunk<CommentDto[], number>(
  'comment/fetchByOrderId',
  async (orderId, { rejectWithValue }) => {
    try {
      return await CommentsService.getByOrderId(orderId)
    } catch (error) {
      return rejectWithValue('Failed to fetch comments')
    }
  },
)

const commentSlice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    clearComments: (state) => {
      state.comments = []
      state.error = null
    },
    setComments: (state, action: PayloadAction<CommentDto[]>) => {
      state.comments = action.payload
    },
  },
  extraReducers: (builder) => {
    // Create comment
    builder.addCase(doCreateComment.pending, (state) => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(doCreateComment.fulfilled, (state, action) => {
      state.isLoading = false
      state.comments.push(action.payload)
    })
    builder.addCase(doCreateComment.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Update comment
    builder.addCase(doUpdateComment.pending, (state) => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(doUpdateComment.fulfilled, (state, action) => {
      state.isLoading = false
      const index = state.comments.findIndex((c) => c.id === action.payload.id)
      if (index !== -1) {
        state.comments[index] = action.payload
      }
    })
    builder.addCase(doUpdateComment.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Delete comment
    builder.addCase(doDeleteComment.pending, (state) => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(doDeleteComment.fulfilled, (state, action) => {
      state.isLoading = false
      state.comments = state.comments.filter(
        (c) => c.id !== action.payload.commentId,
      )
    })
    builder.addCase(doDeleteComment.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Fetch comments by order ID
    builder.addCase(doFetchCommentsByOrderId.pending, (state) => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(doFetchCommentsByOrderId.fulfilled, (state, action) => {
      state.isLoading = false
      state.comments = action.payload
    })
    builder.addCase(doFetchCommentsByOrderId.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
  },
})

// Selectors - use inline type to avoid circular dependency with RootState
export const selectComments = (state: { comment: CommentState }) =>
  state.comment.comments
export const selectCommentsLoading = (state: { comment: CommentState }) =>
  state.comment.isLoading
export const selectCommentsError = (state: { comment: CommentState }) =>
  state.comment.error

export const { clearComments, setComments } = commentSlice.actions
export default commentSlice.reducer
